import { useId, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Banner } from '@figma/fpl-components';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { API_ROUTES } from '@/api/apiRoutes';
import { safeAxios } from '@/services/safeAxios';
import {
  AppDialogSection,
  AppDialog,
  AppDialogHeader,
  AppDialogTitle,
  AppDialogContent,
  AppDialogActions,
  AppDialogXBtn,
} from '@/UI/AppDialog/AppDialog';
import { TextStdRg, TextSmRg } from '@/UI/AppTypography/AppTypography';
import { Skeleton } from '@/UI/Skeleton/Skeleton';
import { log } from '@/logger/logger.ts';
import { EL_COLORS } from '@/colors';
import { I18N_KEYS } from '@/language/keys';
import { useAnalytics, TrackTypeEnum } from '@/hooks/useAnalytics';
import { FlexCenVer, FlexCol } from '@/UI/styles';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { Dropdown } from '@/UI/Dropdown/Dropdown';
import { Box } from '@/UI/Box/Box';
import { FieldLabel, useLabelFieldOffset } from '@/UI/FieldLabel/FieldLabel';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { useUserStore } from '@/state/user.state';
import { useGlobalStore } from '@/state/global.state';
import { WarningCircleIcon } from '@/UI/Icons/WarningCircleIcon';
import { CheckMarkCircleIcon } from '@/UI/Icons';
import { useChangeRecipeOwner } from '@/queries/useRecipes';

const logger = log.getLogger('ChangeOwnerModal');

type TeamMember = {
  id: string;
  name: string;
  email: string;
};
interface ChangeOwnerModalProps {
  open: boolean;
  onClose: () => void;
  recipeId: string | null;
}

const ChangeOwnerModal = ({ open, onClose, recipeId }: ChangeOwnerModalProps) => {
  const selectOwnerId = useId();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);

  const currentUser = useUserStore((state) => state.user);
  const [isLoadingTeamMembers, setIsLoadingTeamMembers] = useState(false);
  const [isLoadingChangeOwner, setIsLoadingChangeOwner] = useState(false);

  const { mutateAsync: changeOwner } = useChangeRecipeOwner();
  const { updateSnackbarData } = useGlobalStore();
  const { track } = useAnalytics();
  const { t } = useTranslation();
  const isFpl = useFplMigrationFlag();
  const labelFieldOffset = useLabelFieldOffset();

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleChangeOwner = useCallback(
    (option: { id: string }) => {
      setSelectedMember(teamMembers.find((member) => member.id === option.id) || null);
    },
    [teamMembers],
  );

  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        if (!currentUser?.uid) return;
        setIsLoadingTeamMembers(true);
        const res = await safeAxios.get(API_ROUTES.workspaces.root());
        setTeamMembers(
          [
            ...(res.data?.members.filter((member) => member.status === 'active' && member.id !== currentUser?.uid) ||
              []),
          ].sort((a, b) => a.name.localeCompare(b.name)),
        );
      } catch (e) {
        logger.error('Error fetching team members', e);
      } finally {
        setIsLoadingTeamMembers(false);
      }
    };
    void fetchTeamMembers();
  }, [currentUser?.uid]);

  const handleConfirmChangeOwner = useCallback(async () => {
    if (!recipeId || !selectedMember?.id) return;
    setIsLoadingChangeOwner(true);
    try {
      await changeOwner({
        id: recipeId,
        newOwnerId: selectedMember.id,
        newOwnerName: selectedMember.name || selectedMember.email,
      });
      track(TrackTypeEnum.BI, {
        event: 'Recipe Owner Changed',
      });
      updateSnackbarData({
        text: t(I18N_KEYS.MAIN_DASHBOARD.CHANGE_OWNER_MODAL.SUCCESS_SNACKBAR),
        isOpen: true,
        icon: <CheckMarkCircleIcon width={20} height={20} />,
      });
      handleClose();
    } catch (e) {
      logger.error('Error changing owner', e);
      updateSnackbarData({
        text: t(I18N_KEYS.MAIN_DASHBOARD.CHANGE_OWNER_MODAL.ERROR_SNACKBAR),
        isOpen: true,
        icon: null,
        severity: 'error',
      });
    } finally {
      setIsLoadingChangeOwner(false);
    }
  }, [recipeId, selectedMember, changeOwner, track, updateSnackbarData, t, handleClose]);

  return (
    <AppDialog
      data-testid="share-workflow-modal"
      open={open}
      onClose={handleClose}
      aria-labelledby="share-design-app-modal"
      width="md"
      slotProps={{
        paper: {
          sx: {
            background: token.bg,
            border: `1px solid ${EL_COLORS.BoxBorder}`,
            borderRadius: 2,
          },
        },
      }}
    >
      <AppDialogSection>
        <AppDialogHeader component="div" sx={{ p: 3, pt: 6, pb: 0, position: 'relative' }}>
          <AppDialogXBtn onClick={handleClose} sx={{ position: 'absolute', top: 12, right: 12 }} />
          <AppDialogTitle>{t(I18N_KEYS.MAIN_DASHBOARD.CHANGE_OWNER_MODAL.TITLE)}</AppDialogTitle>
        </AppDialogHeader>

        <AppDialogContent sx={{ px: 3, pb: 0, mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <TextStdRg color={token.text} sx={{ mb: 2, display: 'block' }}>
            {t(I18N_KEYS.MAIN_DASHBOARD.CHANGE_OWNER_MODAL.SUBTITLE)}
          </TextStdRg>
          <FlexCol sx={{ position: 'relative', mb: 2 }}>
            <FieldLabel htmlFor={selectOwnerId}>
              {`${t(I18N_KEYS.MAIN_DASHBOARD.CHANGE_OWNER_MODAL.SELECT_OWNER)} *`}
            </FieldLabel>
            <Box sx={{ mt: labelFieldOffset }}>
              {isLoadingTeamMembers ? (
                <Skeleton variant="text" width="100%" height={32} />
              ) : (
                <Dropdown
                  id={selectOwnerId}
                  size="large"
                  sx={{ height: 32 }}
                  width="100%"
                  placeholder={t(I18N_KEYS.MAIN_DASHBOARD.CHANGE_OWNER_MODAL.SELECT_OWNER_PLACEHOLDER)}
                  options={teamMembers.map((member) => ({
                    label: member.name || member.email,
                    value: member.id,
                    id: member.id,
                  }))}
                  value={selectedMember?.id}
                  onChange={handleChangeOwner}
                />
              )}
            </Box>
          </FlexCol>
          {isFpl ? (
            <Banner.InsetModal variant="default">
              <Banner.Message>{t(I18N_KEYS.MAIN_DASHBOARD.CHANGE_OWNER_MODAL.WARNING)}</Banner.Message>
            </Banner.InsetModal>
          ) : (
            <FlexCenVer sx={{ gap: 1, background: token.bgTransparent, px: 1.5, py: 1, borderRadius: 1 }}>
              <WarningCircleIcon width={14} height={14} color={token.textSecondary} />
              <TextSmRg sx={{ color: token.textSecondary }}>
                {t(I18N_KEYS.MAIN_DASHBOARD.CHANGE_OWNER_MODAL.WARNING)}
              </TextSmRg>
            </FlexCenVer>
          )}
        </AppDialogContent>

        <AppDialogActions sx={{ justifyContent: 'flex-end', px: 3, pb: 3, pt: 4 }}>
          <ButtonContained mode="outlined" onClick={handleClose} size="small" sx={{ px: 2 }}>
            {t(I18N_KEYS.MAIN_DASHBOARD.CHANGE_OWNER_MODAL.CANCEL)}
          </ButtonContained>
          <ButtonContained
            mode="filled-light"
            onClick={() => {
              void handleConfirmChangeOwner();
            }}
            disabled={teamMembers.length === 0 || !selectedMember}
            loading={isLoadingChangeOwner}
            sx={{ px: 2 }}
            size="small"
          >
            {t(I18N_KEYS.MAIN_DASHBOARD.CHANGE_OWNER_MODAL.CONFIRM)}
          </ButtonContained>
        </AppDialogActions>
      </AppDialogSection>
    </AppDialog>
  );
};

export default ChangeOwnerModal;
